import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormObjectType, JoinFormType } from 'src/types/react-hook-form'
import { KTMVNOVerifySpace } from 'src/types/api'
import { useModal } from 'src/components/Modal/provider'
import { Button } from 'src/pages/Rop04JoinFormPage/components/Button'
import Lottie from 'lottie-react'
import ArrowLottie from 'src/assets/images/rop03/arrow.json'
import checkbox from 'src/assets/images/rop03/checkbox.svg'
import nonCheckbox from 'src/assets/images/rop03/nonCheckbox.svg'

export interface KTMVNOModalChildrenProps extends KTMVNOVerifySpace.Response {
  ktMVNOAttribute: Partial<FormObjectType>
  otpCertRequest: () => void
  isLoading: boolean
}

const KTMVNOModalChildren = ({
  Contents,
  Title,
  ktMVNOAttribute,
  otpCertRequest,
  isLoading,
}: KTMVNOModalChildrenProps) => {
  const { closeModal } = useModal()
  const { register, setValue } = useFormContext<JoinFormType>()

  const valueKtMVNO = useWatch({ name: 'KTMVNO' })

  let isReady = false
  const timer = setTimeout(() => {
    isReady = true
  }, 100)

  const handleSubmit = () => {
    if (!!valueKtMVNO) {
      closeModal()
      otpCertRequest()
    } else {
      setValue('KTMVNO', true)
    }
  }

  const handleCancel = () => {
    setValue('KTMVNO', false)
    closeModal()
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isReady && !isLoading) {
        handleSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <JoinKTMVNOModalContainer data-lenis-prevent-wheel data-lenis-prevent-touch>
      <ModalTitle>
        KT MVNO(알뜰폰)
        <br />
        추가 약관 동의가 필요합니다.
      </ModalTitle>
      <ModalDesc>아래 약관 확인 후 동의해주세요.</ModalDesc>
      <JoinKTMVNOModalContents>
        <MvnoTermsContents>
          <p dangerouslySetInnerHTML={{ __html: Contents }} />
        </MvnoTermsContents>
      </JoinKTMVNOModalContents>
      <JoinTermsItem>
        <MvnoFormCheckboxLabel>
          <KtMvnoCheckbox {...register('KTMVNO', ktMVNOAttribute.register_options)} />
          {Title}
        </MvnoFormCheckboxLabel>
      </JoinTermsItem>
      <ButtonWrap>
        <Button.Default onClick={handleCancel}>취소</Button.Default>
        <Button.JoinSubmit
          type="button"
          name="동의 및 인증번호 받기"
          onClick={handleSubmit}
          icon={<Lottie animationData={ArrowLottie} loop autoplay />}
          iconAlt=""
          iconSize={26}
          isLoading={isLoading}
        />
      </ButtonWrap>
    </JoinKTMVNOModalContainer>
  )
}
export default KTMVNOModalChildren

const JoinKTMVNOModalContainer = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 12px;
`
const ModalTitle = styled.strong`
  color: #25282e;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`

const ModalDesc = styled.p`
  color: #313338;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`

const JoinKTMVNOModalContents = styled.div`
  display: flex;
  flex-flow: row nowrap;
  padding: 10px;
  width: 100%;
  height: 116px;
  border-radius: 16px;
  overflow: hidden;
  background-color: #fafafa;
`

const MvnoTermsContents = styled.div`
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  overflow-y: scroll;
  color: #717580;
  font-family: 'Pretendard', sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  text-align: left;

  & * {
    color: #717580;
  }

  /* 스크롤바 숨김 */
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const JoinTermsItem = styled.div`
  padding: 15px 16px;
  height: 50px;
  border-radius: 12px;
  background-color: #fafafa;
`
const KtMvnoCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #ccd0d8;
  background-image: url(${nonCheckbox});
  background-position: 30% 30%;
  background-repeat: no-repeat;

  &:checked {
    background-color: #1a6eff;
    background-image: url(${checkbox});
  }
`

const MvnoFormCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #717580;
  font-family: 'Pretendard', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -1.12px;
`

const ButtonWrap = styled.div`
  display: flex;
  gap: 6px;

  & button {
    height: 48px !important;
  }

  & button:nth-child(1) {
    padding: 0 18px;
    width: fit-content;
    word-break: keep-all;
  }

  & button:nth-child(2) {
    padding: 0 16px;
    justify-content: flex-start;
    font-weight: 700;
    letter-spacing: normal;
  }
`
